import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

//MUI
import SaveRounded from '@mui/icons-material/SaveRounded';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import ToggleOn from '@mui/icons-material/VisibilityRounded';
import ToggleOff from '@mui/icons-material/VisibilityOffRounded';

//common
import SchemaForm from 'common/SchemaForm';
import { PASSWORD } from 'config/regex';
import { ElementFormProps, IRawElement } from 'common/SchemaForm/utils';

//screen
import { getPasswordScore } from 'modules/account/detail/tabs/passwordUtils';
import { Password } from 'modules/user-info/services/useUpdatePasswordLoggedInUser';
import { useUserInfoData } from 'modules/user-info/detail/useUserInfoData';

interface ToggleAndStrength {
  currentPassword: {
    toggle: boolean;
    strength: number;
  };
  newPassword: {
    toggle: boolean;
    strength: number;
  };
  newPasswordRetype: {
    toggle: boolean;
    strength: number;
  };
}

type FieldName = 'currentPassword' | 'newPassword' | 'newPasswordRetype';

const ChangePasswordTab = () => {
  const { handleUpdatePassword, password, setPassword } = useUserInfoData();

  const [toggleAndStrength, setToggleAndStrength] = useState<ToggleAndStrength>({
    currentPassword: {
      toggle: false,
      strength: 0,
    },
    newPassword: {
      toggle: false,
      strength: 0,
    },
    newPasswordRetype: {
      toggle: false,
      strength: 0,
    },
  });

  const { formatMessage } = useIntl();

  const handleSetPassword = (field) => {
    const { value, name } = field.target;
    const newState = { ...toggleAndStrength };
    newState[name].strength = getPasswordScore(value);
    setToggleAndStrength(() => newState);
    setPassword({ ...password, [name]: value });
  };

  const togglePassword = (name: FieldName) => {
    const newState = { ...toggleAndStrength };
    newState[name].toggle = !newState[name].toggle;
    setToggleAndStrength(() => newState);
  };

  const isValidPasswordConfirm = (value: string) => {
    if (value === password.newPassword) return true;
    return formatMessage({ id: 'account.error.notSamePassword' });
  };

  const renderField = <F extends FieldName>(
    fieldName: FieldName
  ): ElementFormProps<Password, F> | IRawElement<Password, F> => {
    const isShowingText = toggleAndStrength[fieldName].toggle;
    const label = formatMessage({ id: `user.${fieldName}` });

    return {
      mode: 'text-field',
      type: isShowingText ? 'text' : 'password',
      label: label,
      placeholder: label,
      propsWrapper: { tablet: 6 },
      rules: {
        required: true,
        minLength: {
          value: 8,
          message: formatMessage({ id: 'account.error.minLength' }),
        },
        pattern: {
          value: PASSWORD,
          message: formatMessage({ id: 'account.error.invalidPassword' }),
        },
        validate: fieldName === 'newPasswordRetype' ? isValidPasswordConfirm : undefined,
        onChange: handleSetPassword,
      },
      InputProps: {
        endAdornment: (
          <InputAdornment position="end">
            <IconButton onClick={() => togglePassword(fieldName)} color="primary">
              {isShowingText ? <ToggleOff /> : <ToggleOn />}
            </IconButton>
          </InputAdornment>
        ),
      },
    };
  };

  useEffect(() => {
    // thiết lập password strength nếu có dữ liệu
    setToggleAndStrength((old) => {
      return {
        ...old,
        currentPassword: {
          ...old.currentPassword,
          strength: getPasswordScore(password.currentPassword),
        },
        newPassword: {
          ...old.newPassword,
          strength: getPasswordScore(password.newPassword),
        },
        newPasswordRetype: {
          ...old.newPasswordRetype,
          strength: getPasswordScore(password.newPasswordRetype),
        },
      };
    });
  }, [password]);

  return (
    <SchemaForm<Password>
      formHookProps={{ values: password }}
      onSubmit={handleUpdatePassword}
      formProps={{
        sx: {
          '& .MuiGrid-root.MuiGrid-container': {
            '& .MuiGrid-root.MuiGrid-item': {
              '& .MuiGrid-root.MuiGrid-container': {
                display: 'flex',
                justifyContent: 'center',
              },
            },
          },
        },
      }}
      schema={{
        fields: ({ formProps: { readOnly } }) => {
          return {
            currentPassword: renderField('currentPassword'),
            blank1: { mode: 'node' },
            newPassword: renderField('newPassword'),
            blank3: { mode: 'node' },
            newPasswordRetype: renderField('newPasswordRetype'),
            blank6: { mode: 'node' },
            submit: {
              mode: 'button',
              type: 'submit',
              variant: 'contained',
              startIcon: <SaveRounded />,
              hidden: readOnly,
              children: formatMessage({ id: 'save' }),
              propsWrapper: {
                mobile: true,
                display: 'flex',
                justifyContent: 'flex-end',
              },
            },
          };
        },
      }}
    />
  );
};

export default ChangePasswordTab;
